// External libraries
import { action, actionOn } from 'easy-peasy';
// @ts-expect-error TODO: type or globally ignore react-components
import { isBoolean } from '@checkout/react-components';

// Internal modules
import type { AppMode, GenericObjectType, ValueOf } from '@/types';
import { logger } from '@/lib/logger';
import { features } from '@/lib/features';
import {
  getLocale,
  getUnifiedLoginContextData,
  hasSelectAllSignupTerms,
  isSepaMandateCountry,
} from '@/lib/globals';
import { CountryCodes } from '@/generated/graphql';
import {
  getAddressValidationPayload,
  getBaseAddressFields,
  getCrsFields,
  getDefaultEmailState,
  getDefaultInputState,
  getDefaultKycStateOverride,
  revalidatePassword,
  wasInfoSecFieldChanged,
} from '../utils';
import { ADDRESS_GROUP_REGEX } from '../constants';
import type { DefaultInputState } from '../types';
import { AUTO_COMPLETE_TYPE } from '../types';
import type {
  FormAddressActions,
  FormAddressState,
  FormAddressTypes,
  FormAddressUpdate,
  FormInputActions,
  FormInputState,
  FormModel,
  FormPhoneMetadata,
  FormState,
} from './types';
import {
  SIGNUP_AGREE_TO_PRIVACY_STATEMENTS,
  SIGNUP_AGREE_TO_TERMS,
  THIRD_PARTY_DATA_CONSENT,
  UNIQUE_INFORMATION_PROCESSING,
} from '@/lib/features/constants';

const DEFAULT_PHONE_METADATA: FormPhoneMetadata = {
  isConfirmed: false,
  verificationId: '',
};

// Returns if the Primary Residential Address is in the form
const isResidentialAddressEnabled = () =>
  features.isActive('hasPrimaryResidentialAddress');

/**
 * Returns an object with kycFields
 */
const getKycFields = (): GenericObjectType => {
  const kycFields = features.config.kycFields
    // change first letter to be lower cased
    // @ts-expect-error - TODO: Type feature resolvers
    .map(fieldName => fieldName.charAt(0).toLowerCase() + fieldName.slice(1))
    // @ts-expect-error - TODO: Type feature resolvers
    .reduce((accObj, field) => {
      const override = getDefaultKycStateOverride(field);
      accObj[field] = getDefaultInputState(override);

      return accObj;
    }, {});

  return kycFields;
};

/**
 * @function updateResidentialByBillingAddress
 * @description Replicates the data from the billing to the residential address
 * @param {object} state Current state
 * @param {object} data Address data being updated
 */
const updateResidentialByBillingAddress = <T extends FormAddressTypes>(
  state: FormState,
  data: FormAddressUpdate<T>,
): void => {
  if (!isResidentialAddressEnabled() || !state.residentialAddress) {
    return;
  }

  const { addressType, field, isBatchUpdate } = data;

  // If Residential Address is dirty
  // we cannot update it based on Billing Address
  if (addressType !== 'billingAddress' || state.residentialAddress.isDirty) {
    return;
  }

  if (isBatchUpdate) {
    const billingAddressData = Object.keys(state.billingAddress).reduce(
      (addressData, key) => ({
        ...addressData,
        [key]: {
          // @ts-expect-error TODO: This could throw errors in prod code today
          ...state.billingAddress[key],

          // Force `isDirty` to false as the user didn't update
          // Residential Address
          isDirty: false,
        },
      }),
      {},
    );

    state.residentialAddress = {
      ...state.residentialAddress,
      ...billingAddressData,
    };
    return;
  }

  // @ts-expect-error TODO: This could throw errors in prod code today
  state.residentialAddress[field] = {
    // @ts-expect-error TODO: This could throw errors in prod code today
    ...state[addressType][field],

    // Force `isDirty` to false as the user didn't update
    // Residential Address
    isDirty: false,
  };
};

const updateAddressField = <T extends FormAddressTypes>(
  state: FormState,
  data: FormAddressUpdate<T>,
): void => {
  const { addressType, field, isBatchUpdate, payload } = data;

  if (!ADDRESS_GROUP_REGEX.test(addressType)) {
    return logger.warn('weasley_update_address_invalid_type', {
      addressType,
    });
  }

  if (isBatchUpdate) {
    const entries = Object.entries(payload) as [
      keyof typeof payload,
      ValueOf<typeof payload>,
    ][];

    // We have a few errors, if we make this more specific we won't need them
    // TODO: Refactor to make logic easier to understand and work better w/ TS
    const updatedAddressFields = entries.reduce(
      (accum, [fieldName, fieldData]) => {
        const hasIsDirtyProperty = isBoolean(
          // @ts-expect-error - Not all fields have isDirty, that's ok
          state[addressType]?.[fieldName]?.isDirty,
        );
        const isDirty =
          // @ts-expect-error - Not all fields have isDirty, that's ok
          state[addressType]?.[fieldName]?.isDirty ||
          // @ts-expect-error - Not all fields have isDirty, that's ok
          fieldData.isDirty ||
          // @ts-expect-error - Not all fields have value, that's ok
          Boolean(fieldData.value);

        accum[fieldName] = {
          // @ts-expect-error TODO: This could throw errors in prod code today
          ...state[addressType][fieldName],
          ...fieldData,
          ...(hasIsDirtyProperty && { isDirty }),
        };
        return accum;
      },
      // TODO: Update this to more specific type in the future when we know it
      {} as GenericObjectType,
    );

    state[addressType] = {
      ...state[addressType],
      ...updatedAddressFields,
    };

    updateResidentialByBillingAddress(state, data);

    return;
  }

  // @ts-expect-error - Not all fields have isDirty, that's ok
  const hasIsDirtyProperty = isBoolean(state[addressType]?.[field]?.isDirty);
  const isDirty =
    // @ts-expect-error - Not all fields have isDirty, that's ok
    state[addressType]?.[field]?.isDirty ||
    // @ts-expect-error - Not all fields have isDirty, that's ok
    payload.isDirty ||
    // @ts-expect-error - Not all fields have value, that's ok
    Boolean(payload.value);

  // TODO: updateAddressValidationError does not have a field
  // Avoiding refactors in TS conversion
  // @ts-expect-error TODO: This could throw errors in prod code today
  state[addressType][field] = {
    // @ts-expect-error TODO: This could throw errors in prod code today
    ...state[addressType][field],
    ...payload,
    ...(hasIsDirtyProperty && { isDirty }),
  };

  updateResidentialByBillingAddress(state, data);
};

const updateAutoCompleteType =
  (type: AUTO_COMPLETE_TYPE) =>
  (state: FormState, data: { addressType: FormAddressTypes }) => {
    const isWps = features.isActive('isWps');
    const { addressType } = data;

    logger.info('updateAutoCompleteType', {
      addressType,
      isWps,
      type,
    });

    // TODO: Can we refactor to assign isManipulated intead of spreading?
    // @ts-expect-error See note abouve, avoid refactor in TS conversion
    state[addressType] = {
      ...state[addressType],
      autoCompleteType: {
        value: type,
      },
    };
  };

const updateIsManipulated =
  (value: boolean) =>
  (state: FormState, data: { addressType: FormAddressTypes }) => {
    const { addressType } = data;
    // TODO: Can we refactor to assign isManipulated intead of spreading?
    // @ts-expect-error See note abouve, avoid refactor in TS conversion
    state[addressType] = {
      ...state[addressType],
      isManipulated: {
        value,
      },
    };
  };

const getDefaultOnboardOption = (): DefaultInputState<AppMode> => {
  const isOptionalSignup = features.isActive('isOptionalSignup');
  const isStreamlineOptionalExperience = features.isActive(
    'isStreamlineOptionalExperience',
  );
  const isPoma = features.isActive('isPOMAEligible');
  const isForcedGuest = features.someActive(
    'isWpsGuestUpgradeEligible',
    'isForcedGuest',
  );
  const isPasswordlessForcedSignup = features.isActive(
    'passwordlessForcedSignup',
  );
  const { country } = getLocale();
  const isSignupIneligible =
    features.isActive('shouldDisableSignup') ||
    (features.isActive('shouldDisableSignupForGibraltar') &&
      country === CountryCodes.Gi);

  const { hasExistingAccount } = getUnifiedLoginContextData();
  // This is for SEPA guest flow
  const hasGuestXOSepaUpdate = features.isActive('hasGuestXOSepaUpdate');

  const hasMemberAccount =
    hasExistingAccount &&
    features.isActive('shouldDisableOnboardToggleForMembers');

  // Default for forced signup
  const overrides: DefaultInputState<AppMode> = {
    displayError: false,
    error: '',
    errorCode: '',
    isDirty: false,
    isValid: true,
    value: 'signup',
  };

  // By default, no option is selected for radio buttons
  if (isOptionalSignup) {
    if (isStreamlineOptionalExperience) {
      overrides.errorCode = '';
      overrides.isValid = true;
      overrides.value =
        hasMemberAccount || hasGuestXOSepaUpdate ? 'guest' : 'signup'; // Can assume they don't want to sign up.
    } else {
      overrides.errorCode = 'valueMissing';
      overrides.isValid = false;
      // @ts-expect-error - Why do we assign this to invalid empty value?
      overrides.value = '';
    }
  }

  if (
    isPoma ||
    isSignupIneligible ||
    isForcedGuest ||
    isPasswordlessForcedSignup
  ) {
    overrides.errorCode = '';
    overrides.isValid = true;
    overrides.value = 'guest';
  }

  return getDefaultInputState(overrides);
};

/** Generate a base form state for address fields */
const getAddressState = (): FormAddressState => ({
  billingAddress: {
    ...getBaseAddressFields(),
    autoCompleteType: { value: AUTO_COMPLETE_TYPE.MANUAL },
    isManipulated: { value: false },
    meta: { hasValidationError: false },
  },
  /**
   * Residential address is visible just for some countries
   * These countries are in the experiment FEATURE_RESIDENTIAL_ADDRESS_LITE
   * Its default value is the billing address but the user can change it
   */
  ...(isResidentialAddressEnabled() && {
    residentialAddress: {
      ...getBaseAddressFields(),
      autoCompleteType: { value: AUTO_COMPLETE_TYPE.MANUAL },
      // Managing isDirty on the object to avoid overriding with billingAddress
      // after user changed it
      isDirty: false,
      isManipulated: { value: false },
      meta: { hasValidationError: false },
    },
  }),
  shareAddressWithDonatee: {
    value: true,
  },
  shippingAddress: {
    autoCompleteType: { value: AUTO_COMPLETE_TYPE.MANUAL },
    country: getDefaultInputState({
      errorCode: '',
      isValid: true,
      value: getLocale().country,
    }),
    firstName: getDefaultInputState(),
    isManipulated: { value: false },
    lastName: getDefaultInputState(),
    meta: { hasValidationError: false },
    ...getBaseAddressFields(),
  },
  shipToBilling: {
    hasChanged: false,
    value: true,
  },
  bypassAddressNormalization: false, // eslint-disable-line sort-keys
});

const createAddressActions = (): FormAddressActions => ({
  updateAddressField: action((state, data) => {
    updateIsManipulated(true)(state, data);
    updateAddressField(state, data);
  }),

  updateAddressValidationError: action((state, data) => {
    updateAddressField(state, {
      addressType: data.addressType,
      isBatchUpdate: true,
      payload: getAddressValidationPayload(data),
    });
  }),

  updateAllAddressFieldsUsingANS: action((state, data) => {
    updateAddressField(state, data);
    updateAutoCompleteType(AUTO_COMPLETE_TYPE.ANS)(state, data);
    updateIsManipulated(false)(state, data);
  }),

  updateAllAddressFieldsUsingGoogleAutocomplete: action((state, data) => {
    updateAddressField(state, data);
    updateAutoCompleteType(AUTO_COMPLETE_TYPE.GOOGLE)(state, data);
    updateIsManipulated(false)(state, data);
  }),

  updateAllAddressFieldsUsingMerchantPrefilledData: action((state, data) => {
    updateAddressField(state, data);
    updateAutoCompleteType(AUTO_COMPLETE_TYPE.MERCHANT_PREFILLED)(state, data);
    updateIsManipulated(false)(state, data);
  }),

  updateBypassAddressNormalization: action((state, payload) => {
    state.bypassAddressNormalization = Boolean(payload);
  }),

  updateShareAddressWithDonatee: action((state, payload) => {
    state.shareAddressWithDonatee = { value: payload };
  }),

  updateShipToBilling: action((state, payload) => {
    state.shipToBilling = {
      hasChanged: true,
      value: payload,
    };
  }),

  // Listeners
  // eslint-disable-next-line sort-keys
  bypassAddressNormalizationListener: actionOn(
    // Target resolver
    actions => actions.updateAddressField,
    (state, target) => {
      const { addressType } = target.payload;
      const shipTo = state.shipToBilling.value
        ? 'billingAddress'
        : 'shippingAddress';

      // If the user changes the shipping address then set the
      // bypass address normalization flag to `false`
      if (
        (features.isActive('hasAddressNormalizationShipping') ||
          features.isActive('hasAddressNormalizationBoth')) &&
        addressType === shipTo &&
        state.bypassAddressNormalization
      ) {
        state.bypassAddressNormalization = false;
      }
    },
  ),

  // Set Residential Address as dirty to avoid updating it
  //  based on Billing Address
  updateResidentialAddressIsDirty: action((state, payload) => {
    // Residential Address doesn't exists, so feature is disabled
    if (!state.residentialAddress) {
      return;
    }

    state.residentialAddress.isDirty = payload;
  }),
});

const getInputState = (): FormInputState => {
  const onboardOption = getDefaultOnboardOption();
  const hasSepaMandate = isSepaMandateCountry();
  const hasSelectAllSignupTermsExperience = hasSelectAllSignupTerms();
  return {
    bankAccountNumber: getDefaultInputState(),
    bankAccountType: {
      value: 'iban',
    },
    bankIban: getDefaultInputState(),
    bankSortCode: getDefaultInputState(),
    cardCvv: getDefaultInputState(),
    cardExpiry: getDefaultInputState(),
    cardNumber: getDefaultInputState({
      cardType: '',
      cobrand: '',
    }),
    cardProductClass: getDefaultInputState(),
    ...(features.isActive('shouldDisplayJpKanaAndKanjiNames') && {
      countrySpecificFirstName: getDefaultInputState(),
      countrySpecificLastName: getDefaultInputState(),
    }),
    currencyConversionSelection: '',
    dialingCode: getDefaultInputState({
      errorCode: '',
      isValid: true,
      value: getLocale().country,
    }),
    email: getDefaultEmailState(),
    firstName: getDefaultInputState(),
    guestAgreeToTerms: getDefaultInputState({
      isValid: features.isNotActive('hasGuestTermsAndConditionsCheckbox'),
      value: false,
    }),
    initialOnboardOption: onboardOption,
    installmentTerm: getDefaultInputState(),
    isSignupIncentiveOptIn: false,
    lastName: getDefaultInputState(),
    marketingOptIn: getDefaultInputState({
      errorCode: '',
      isValid: true,
      value:
        !features.isActive('hasMarketingOptInCheckbox') ||
        features.isActive('isMarketingOptInCheckedByDefault'),
    }),
    onboardOption: onboardOption,
    password: getDefaultInputState(),
    phone: {
      ...getDefaultInputState(),
      meta: DEFAULT_PHONE_METADATA,
    },
    phoneType: getDefaultInputState({
      errorCode: '',
      isValid: true,
      value: 'MOBILE',
    }),
    residentialAddressButtonShouldSubmit: false,
    sepaMandate: getDefaultInputState({
      isValid: !hasSepaMandate,
      value: false,
    }),
    shippingInsuranceSelected: false,
    [SIGNUP_AGREE_TO_TERMS]: getDefaultInputState({
      isValid:
        features.isNotActive('hasSignupTermsAndConditionsCheckbox') &&
        !hasSelectAllSignupTermsExperience,
      value: false,
    }),
    // Right of Withdraw checkbox, just exists for countries in
    // TREATMENT_2 of FEATURE_SIGNUP_RIGHT_OF_WITHDRAW
    ...(features.isActive('signupRightOfWithdrawalCheckbox') && {
      rightOfWithdrawal: getDefaultInputState({
        isValid: false,
        value: false,
      }),
    }),
    // Privacy Statement checkbox
    ...(features.isActive('hasPrivacyStatementTerms') && {
      [SIGNUP_AGREE_TO_PRIVACY_STATEMENTS]: getDefaultInputState({
        isValid: false,
        value: false,
      }),
    }),
    // Third party data consent checkbox (KR)
    ...(features.isActive('hasThirdPartyDataConsentCheckbox') && {
      [THIRD_PARTY_DATA_CONSENT]: getDefaultInputState({
        isValid: false,
        value: false,
      }),
    }),
    // Unique information processing checkbox (KR)
    ...(features.isActive('hasUniqueInformationProcessingCheckbox') && {
      [UNIQUE_INFORMATION_PROCESSING]: getDefaultInputState({
        isValid: false,
        value: false,
      }),
    }),
    ...getKycFields(),
    ...getCrsState(),
  };
};

const getCrsState = () => {
  if (!features.isActive('shouldShowCRSInterstitial')) {
    return {};
  }

  return {
    bypassCRSInterstitial: false,
    crsLegalFirstName: getDefaultInputState({
      errorCode: '',
      isValid: true,
      value: '',
    }),
    crsLegalLastName: getDefaultInputState({
      errorCode: '',
      isValid: true,
      value: '',
    }),
    crsTaxDetails: {
      value: [getCrsFields()],
    },
    crsTaxStatus: getDefaultInputState({
      errorCode: '',
      isValid: true,
      value: null,
    }),
  };
};

const createInputActions = (): FormInputActions => ({
  updateCurrencyConversionSelection: action((state, selection) => {
    state.currencyConversionSelection = selection;
  }),

  updateNonAddressField: action((state, { field, payload }) => {
    const isDirty =
      state[field]?.isDirty || payload.isDirty || Boolean(payload.value);
    state[field] = {
      ...state[field],
      ...payload,
      // @ts-expect-error: TODO: update code to check if this is a input
      // avoid refactors in TS conversion
      isDirty,
    };
  }),

  updateOnboardOption: action((state, option) => {
    state.onboardOption.value = option;
    state.onboardOption.isValid = true;
    state.onboardOption.errorCode = '';
  }),

  updateShippingInsuranceSelected: action((state, payload) => {
    state.shippingInsuranceSelected = Boolean(payload);
  }),

  // eslint-disable-next-line sort-keys
  onUpdateNonAddressField: actionOn(
    actions => actions.updateNonAddressField,
    (state, target) => {
      const { country } = getLocale();
      if (
        country === CountryCodes.Au &&
        wasInfoSecFieldChanged(target.payload.field)
      ) {
        const { errors, isValid } = revalidatePassword(state);

        if (isValid !== state.password.isValid) {
          state.password = {
            ...state.password,
            displayError: Boolean(state.displayError) && !isValid,
            errorCode: errors[0] || '',
            isValid,
          };
        }
      }
    },
  ),

  onUpdateStreetNameAndHouseNumber: actionOn(
    actions => actions.updateAddressField,
    (state, target) => {
      const isStreetNameOrHouseNumberField = [
        'streetName',
        'houseNumber',
        // @ts-expect-error
        // TODO: Refactor TS error due to TS knowing it can only be a string
      ].includes(target.payload.field);
      const addressType = target.payload.addressType;

      if (!isStreetNameOrHouseNumberField || !state[addressType]) {
        return;
      }

      const { houseNumber, streetName } = state[addressType] || {};
      const newLine1 = `${streetName?.value}, ${houseNumber?.value}`;
      // @ts-expect-error: Residential Address might be undefined
      state[addressType].line1 = {
        // @ts-expect-error: Residential Address might be undefined
        ...state[addressType].line1,
        isDirty: true,
        isValid: (houseNumber?.isValid && streetName?.isValid) ?? false,
        value: newLine1,
      };
    },
  ),

  resetNamesErrorOnChangeNationality: actionOn(
    actions => actions.updateNonAddressField,
    (state, target) => {
      if (target.payload.field !== 'nationality') {
        return;
      }

      const defaultValues = {
        displayError: false,
        error: '',
        errorCode: '',
        isValid: true,
      };

      const firstNameField = state.firstName;
      if (
        firstNameField.errorCode === 'INVALID_FIRST_NAME' &&
        firstNameField.displayError
      ) {
        state.firstName = {
          ...state.firstName,
          ...defaultValues,
        };
      }

      const lastNameField = state.lastName;
      if (
        lastNameField.errorCode === 'INVALID_LAST_NAME' &&
        lastNameField.displayError
      ) {
        state.lastName = {
          ...state.lastName,
          ...defaultValues,
        };
      }
    },
  ),

  resetPhoneMetadataWhenValueChanged: actionOn(
    actions => actions.updateNonAddressField,
    (state, target) => {
      const isConfirmedPhoneUpdateAction =
        // @ts-expect-error Looks like there may be an issue in easy-peasy d.ts
        // Notice pyload.payload type ahead
        // TODO: Verify this is an issue in easy-peasy types and look into fix
        target.payload.field === 'phone' && target.payload.meta === undefined;

      if (!isConfirmedPhoneUpdateAction) {
        state.phone = {
          ...state.phone,
          meta: DEFAULT_PHONE_METADATA,
        };
      }
    },
  ),

  updateBypassCRSInterstitial: action((state, payload) => {
    state.bypassCRSInterstitial = Boolean(payload);
  }),
  updateIsSignupIncentiveOptIn: action((state, payload) => {
    state.isSignupIncentiveOptIn = Boolean(payload);
  }),
  updateResidentialAddressButtonShouldSubmit: action((state, payload) => {
    state.residentialAddressButtonShouldSubmit = Boolean(payload);
  }),
});

const getFormState = <T extends GenericObjectType>(
  overrides = {} as T,
): FormState & T => ({
  ...getAddressState(),
  ...getInputState(),
  ...overrides,
});

const formModelFactory = (): FormModel => ({
  ...getFormState(),
  ...createAddressActions(),
  ...createInputActions(),
});

export { formModelFactory, getFormState, getKycFields, AUTO_COMPLETE_TYPE };
